import { useEffect, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  AudioLines,
  Check,
  CheckCircle2,
  GitBranch,
  Loader2,
  LockKeyhole,
  Sparkles,
  Waypoints,
} from "lucide-react";
import { api } from "./api";

export function EntryFrame({ children }: { children: ReactNode }) {
  return (
    <div className="entry-shell">
      <section className="entry-story">
        <a className="brand" href="/">
          <span className="brand-mark">
            <Waypoints size={23} />
          </span>
          workroom<span className="brand-dot">.</span>
        </a>
        <div className="entry-message">
          <div className="eyebrow">A SHARED SENSE OF DIRECTION</div>
          <h1>
            Good work starts
            <br />
            on the same page<span>.</span>
          </h1>
          <p>
            Your conversations become a shared plan.
            <br />
            The context stays. The next move gets clearer.
          </p>
          <div className="entry-network" aria-hidden="true">
            <span className="network-person">
              C<small>Charlton</small>
            </span>
            <span className="network-line" />
            <span className="network-center">
              <Waypoints size={34} />
            </span>
            <span className="network-line" />
            <span className="network-person david">
              D<small>David</small>
            </span>
          </div>
          <div className="entry-principles">
            <span>
              <AudioLines size={17} />
              Keep the context
            </span>
            <span>
              <GitBranch size={17} />
              Clear the handoffs
            </span>
            <span>
              <Sparkles size={17} />
              Move with purpose
            </span>
          </div>
        </div>
        <div className="entry-footnote">
          <LockKeyhole size={13} />A private workspace for Charlton and David.
          <span>SENSE SYSTEMS</span>
        </div>
      </section>
      <main className="entry-main">
        {children}
        <div className="entry-main-footer">A little less noise. A clear next move.</div>
      </main>
    </div>
  );
}
type SetupDetails = {
  name: string;
  username: string;
  timezone: string;
  complete: boolean;
};
export function Onboarding({
  temporaryPassword,
  onComplete,
}: {
  temporaryPassword: string;
  onComplete: () => void;
}) {
  const query = useQuery({
    queryKey: ["onboarding"],
    queryFn: () => api<SetupDetails>("/onboarding"),
    retry: false,
  });
  const [step, setStep] = useState(0);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [currentPassword, setCurrentPassword] = useState(temporaryPassword);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  useEffect(() => {
    if (query.data) {
      setName(query.data.name);
      setUsername(query.data.username);
    }
  }, [query.data]);
  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("Your new passwords do not match.");
      return;
    }
    setPending(true);
    try {
      await api("/onboarding", {
        method: "POST",
        body: JSON.stringify({
          name,
          username,
          timezone,
          currentPassword,
          newPassword,
          confirmPassword,
        }),
      });
      await api("/auth/sign-in/username", {
        method: "POST",
        body: JSON.stringify({ username, password: newPassword }),
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setStep(2);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Setup could not be completed. Please try again.");
    } finally {
      setPending(false);
    }
  }
  if (query.error)
    return (
      <EntryFrame>
        <div className="entry-card">
          <h2>Let’s reconnect.</h2>
          <p>{query.error.message}</p>
          <button className="button primary" onClick={() => location.reload()}>
            Try again
          </button>
        </div>
      </EntryFrame>
    );
  if (!query.data)
    return (
      <EntryFrame>
        <div className="entry-card">
          <Loader2 className="spin" />
          <h2>Getting your workspace ready.</h2>
        </div>
      </EntryFrame>
    );
  const firstName = name.trim().split(" ")[0] || query.data.name;
  return (
    <EntryFrame>
      <div className={`entry-card onboarding-card step-${step}`}>
        <div className="setup-progress" aria-label={`Setup step ${step + 1} of 3`}>
          {["Welcome", "Make it yours", "Ready to go"].map((label, i) => (
            <span className={i <= step ? "active" : ""} key={label}>
              <i>{i < step ? <Check size={11} /> : i + 1}</i>
              {label}
            </span>
          ))}
        </div>
        {step === 0 && (
          <>
            <div className="welcome-symbol">
              <Waypoints size={31} />
            </div>
            <div className="eyebrow">YOUR PLACE IS READY</div>
            <h2>
              Welcome, {firstName}
              <span>.</span>
            </h2>
            <p>
              You’re joining a workroom built around the way you and{" "}
              {query.data.username === "david" ? "Charlton" : "David"} work together. Let’s make it
              yours.
            </p>
            <div className="welcome-list">
              <div>
                <AudioLines size={19} />
                <span>
                  <strong>The whole conversation, kept.</strong>
                  <small>Meetings and original context, together in one place.</small>
                </span>
              </div>
              <div>
                <GitBranch size={19} />
                <span>
                  <strong>A clear next move for each of you.</strong>
                  <small>See the work and the handoffs that move it forward.</small>
                </span>
              </div>
              <div>
                <Sparkles size={19} />
                <span>
                  <strong>Less work about the work.</strong>
                  <small>Share an update. The plan follows the conversation.</small>
                </span>
              </div>
            </div>
            <button className="button primary entry-submit" onClick={() => setStep(1)}>
              Set up my account <ArrowRight size={16} />
            </button>
            <div className="entry-small">
              About a minute. Your temporary password will be replaced.
            </div>
          </>
        )}
        {step === 1 && (
          <>
            <div className="eyebrow">JUST A FEW DETAILS</div>
            <h2>
              Make yourself at home<span>.</span>
            </h2>
            <p>Choose how you’ll sign in and the time zone you work in.</p>
            <form
              onSubmit={(event) => {
                void submit(event);
              }}
            >
              <div className="setup-grid">
                <label className="form-label">
                  Your name
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    maxLength={80}
                    autoComplete="name"
                  />
                </label>
                <label className="form-label">
                  Username
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase())}
                    required
                    minLength={3}
                    maxLength={30}
                    pattern="[a-z0-9_]+"
                    autoComplete="username"
                  />
                </label>
              </div>
              <label className="form-label">
                Your time zone
                <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                  {[...new Set([timezone, "UTC", ...Intl.supportedValuesOf("timeZone")])].map(
                    (zone) => (
                      <option key={zone} value={zone}>
                        {zone.replaceAll("_", " ")}
                      </option>
                    ),
                  )}
                </select>
              </label>
              {!temporaryPassword && (
                <label className="form-label">
                  Temporary password
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </label>
              )}
              <div className="setup-grid">
                <label className="form-label">
                  New password
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={12}
                    maxLength={128}
                    autoComplete="new-password"
                  />
                </label>
                <label className="form-label">
                  Confirm new password
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError("");
                    }}
                    required
                    minLength={12}
                    maxLength={128}
                    autoComplete="new-password"
                  />
                </label>
              </div>
              <div className={`password-hint ${newPassword.length >= 12 ? "valid" : ""}`}>
                <CheckCircle2 size={13} />
                At least 12 characters. A password manager works well.
              </div>
              {error && (
                <p className="inline-alert" role="alert">
                  {error}
                </p>
              )}
              {error && (
                <button
                  type="button"
                  className="text-button"
                  onClick={() => {
                    void api("/auth/sign-out", { method: "POST", body: "{}" })
                      .then(() => location.reload())
                      .catch((e) =>
                        setError(
                          e instanceof Error ? e.message : "Could not reconnect. Try again.",
                        ),
                      );
                  }}
                >
                  Already saved your account? Return to sign in with your new password.
                </button>
              )}
              <button className="button primary entry-submit" disabled={pending}>
                {pending ? <Loader2 className="spin" size={16} /> : <LockKeyhole size={15} />}{" "}
                {pending ? "Making it yours…" : "Save my account"}
              </button>
            </form>
            <div className="entry-small">
              Your new password replaces the temporary login. You’ll sign in with your username and
              password.
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <div className="welcome-symbol success">
              <Check size={31} />
            </div>
            <div className="eyebrow">YOU’RE ON THE SAME PAGE</div>
            <h2>
              You’re all set, {firstName}
              <span>.</span>
            </h2>
            <p>
              Your account is yours. Start with <strong>Now</strong> to see what needs your
              attention, or open a workstream to see the bigger picture.
            </p>
            <div className="welcome-ready">
              <span>
                <CheckCircle2 size={16} />
                Your own secure sign-in
              </span>
              <span>
                <CheckCircle2 size={16} />
                {timezone.replaceAll("_", " ")}
              </span>
              <span>
                <CheckCircle2 size={16} />
                Conversations and shared work, ready
              </span>
            </div>
            <button className="button primary entry-submit" onClick={onComplete}>
              Open my workspace <ArrowRight size={16} />
            </button>
            <div className="entry-small">
              Welcome to Workroom. Let’s move the right things forward.
            </div>
          </>
        )}
      </div>
    </EntryFrame>
  );
}
